/**
 * SSRS stores a DateTime parameter as UK-format "dd/MM/yyyy HH:mm:ss".
 *
 * That is not a guess: of the date-shaped values in Subscriptions.Parameters on MANDW01, several
 * begin with a component above 12 ("17/10/2022", "30/01/2025", "31/12/2022"), which only parses
 * day-first, and none are ISO. The report server's own REST API disagrees with itself here — it
 * answers a parameter's resolved default in round-trip ISO — but the portal's backend rewrites
 * every DateTime value crossing that boundary (see PbirsDateTimeFormat.cs), so anything reaching
 * this module, stored or resolved, is already day-first by the time it gets here. Emitting
 * anything else — an ISO date especially — hands the report a string it will either misread or
 * reject.
 *
 * Shared between the date picker (DateField) and the default-detail panel, which both need to
 * pull the calendar date back out of that one format without disagreeing on what counts as valid.
 */
const SSRS_DATE = /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/;

export function parseSsrsDate(value: string): { date: string; time: string } | null {
  const m = SSRS_DATE.exec(value.trim());
  if (!m) return null;

  const [, dd, mm, yyyy, hh, mi, ss] = m;
  const day = Number(dd), month = Number(mm);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  return { date: `${yyyy}-${mm}-${dd}`, time: hh ? `${hh}:${mi}:${ss}` : '00:00:00' };
}

/**
 * The OTHER spelling of a date that exists in dbo.Subscriptions.Parameters: .NET's en-US general
 * format, `M/d/yyyy h:mm:ss tt`.
 *
 * Not every subscription was written by something day-first. Measured on MANDW01: of the 17
 * `/Sales/Scope 3` subscriptions that store a ScopeVersion, 9 hold `01/01/1900 00:00:00` and 8 hold
 * `1/1/1900 12:00:00 AM` — the same instant, spelled by an en-US `DateTime.ToString()`. Two more
 * live elsewhere (`FromDate 1/1/2022 4:51:24 PM`, and `10/1/2017 12:00:00 AM` on
 * /Sales/US Sales - ACA Whitelist).
 *
 * <b>The AM/PM designator is what makes this safe to recognise.</b> The day-first format SSRS
 * itself writes is 24-hour and has no designator, so a value carrying one cannot be day-first and
 * there is no ambiguity to resolve: `10/1/2017 12:00:00 AM` is 1 October, not 10 January, because
 * only the en-US formatter produces that string at all. Without the designator this would be
 * unguessable, which is exactly why the pattern requires it rather than falling back to
 * `Date.parse` — the browser would happily read `30/01/2025` as a US date and return Invalid Date,
 * or worse, silently swap day and month on `01/02/2025`.
 */
const US_GENERAL_DATE =
  /^(\d{1,2})\/(\d{1,2})\/(\d{4})[ T](\d{1,2}):(\d{2}):(\d{2})\s*([AaPp])\.?[Mm]\.?$/;

/**
 * A date value rewritten into the one spelling the resolved option list uses, or the value
 * untouched when it is not a date in the other spelling.
 *
 * Used ONLY when deciding whether a stored value matches an offered option — never to rewrite what
 * is stored, and never on the removal path. See `optionFor`.
 */
export function canonicaliseSsrsDate(value: string): string {
  const m = US_GENERAL_DATE.exec(value.trim());
  if (!m) return value;

  const [, month, day, yyyy, hour, mi, ss, meridiem] = m;
  const monthNum = Number(month), dayNum = Number(day), hourNum = Number(hour);
  if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31 || hourNum < 1 || hourNum > 12) return value;

  // 12am is midnight and 12pm is noon — the two cases where adding or subtracting 12 is wrong.
  const pm = meridiem.toLowerCase() === 'p';
  const hour24 = hourNum === 12 ? (pm ? 12 : 0) : (pm ? hourNum + 12 : hourNum);

  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${pad(dayNum)}/${pad(monthNum)}/${yyyy} ${pad(hour24)}:${mi}:${ss}`;
}
